import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { fetchCurrentUser } from '../../../services/auth';
import { usePosts } from '../../../hooks/usePosts';
import { useProfilePicture } from '../../../hooks/useProfilePicture';
import { useProfileCover } from '../../../hooks/useProfileCover';
import useUserProfileSummary from '../../../hooks/useUserProfileSummary';
import useUserBands from '../../../hooks/useUserBands';
import useProfileShare from '../../../hooks/useProfileShare';
import useProfileMediaLibrary from '../../../hooks/useProfileMediaLibrary';
import useProfileTrackPlayback from '../../../hooks/useProfileTrackPlayback';
import useEventEditorActions from '../../../hooks/useEventEditorActions';
import useUserFollowStatus from '../../../hooks/useUserFollowStatus';
import { useUserPreferences } from '../../../store/UserPreferencesContext';
import friendshipService from '../../../services/friendshipService';
import userService from '../../../services/userService';
import { eventService } from '../../../services/eventService';
import {
  serializeInstrumentExperiences,
} from '../../../utils/instrumentExperience';
import {
  BANDS_VIEW_MODE_STORAGE_KEY,
  BANDS_VIEW_MODE_WELCOME,
} from '../../../constants/bands';
import {
  AUDIO_MEDIA_ACCEPT,
  PROFILE_TRACK_MAX_SIZE_MB,
} from '../../../constants/media';
import {
  PROFILE_PREFERRED_STYLE_EMPTY_LABEL,
  PROFILE_PREFERRED_STYLE_INPUT_PLACEHOLDER,
} from '../../../constants/profile';
import { ACTIVE_TAB_STORAGE_KEY, NAVIGATE_TAB_EVENT, TAB_IDS } from '../../../constants/navigation';
import { CANONICAL_VISIBILITY, parseCanonicalVisibility } from '../../../constants/visibility';
import { ENTITY_TIMELINE_PAGE_LIMIT, normalizeEntityTimelineItems } from '../../../utils/entityTimeline';
import { buildBandPath } from '../../../utils/bandRoutes';
import { enqueueProfileFieldSave } from '../../../utils/profileFieldSaveQueue';
import { buildScheduleEventRows } from '../../../utils/scheduleEventRows';
import { isCanonicalObjectId } from '../../../utils/scheduledEntity';
import {
  PROFILE_EDITORIAL_SECTION_IDS as SECTION_IDS,
  applyTrackPlaysCountByIdentity,
  buildProfileEditorialTabs,
  buildProfileEditorialViewModel,
  getMediaPhotoIdentity,
  getTrackIdentity,
  moveTrackByIdentity,
  normalizeTrackPlaysCount,
  reorderMediaPhotosByIdentity,
  sortMediaPhotosNewestFirst,
  stripFileExtension,
} from '../../../utils/profileEditorial';
import { resolveUserDisplayName } from '../../../utils/userIdentity';
import { isBillingUiEnabled } from '../../../constants/billingFeatures';
import { fetchCurrentBillingSubscription, fetchPublicBillingPlans } from '../../../services/api/billing';
import {
  billingCatalogPlans,
  getBillingSubscriptionPlanCode,
  isHomeAudioVisibleLimitKnown,
  isFreeBillingSubscription,
  normalizeBillingCode,
  resolveHomeAudioVisibleLimit,
} from '../../../utils/billingEntitlements';

import ChangeProfilePictureModal from '../../molecules/ProfilePictureUpload';
import ProfilePicturePreviewModal from '../../molecules/ProfilePicturePreviewModal';
import LocationModal from '../../molecules/LocationModal';
import EventCreateModal from '../../molecules/EventCreateModal';
import ConfirmDeleteDialog from '../../molecules/ConfirmDeleteDialog';
import Notification from '../../organisms/Notifications';
import ProfileOverview from '../../organisms/ProfileOverview';
import { userProfileService } from '../../../services/userProfileService';
import {
  deleteProfileTrack,
  deleteUserMediaPhoto,
  reorderProfileTracks,
  reorderUserMediaPhotos,
  uploadUserMediaPhoto,
  uploadProfileTrack,
} from '../../../services/mediaService';

import { PencilIcon } from '../../atoms/icons/EditorialProfileIcons';
import EditorialIconButton from '../../atoms/EditorialIconButton';
import ProfileEditorialView from '../../organisms/HomeEditorial/ProfileEditorialView';

const PHOTO_ACCEPT = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
].join(',');
const INSTRUMENT_EXPERIENCE_ANCHOR_ID = 'home-instrument-experience';
const HOME_FOLLOWERS_RAIL_LIMIT = 24;

function isUserOrganizedEvent(item) {
  const objectType = typeof item?.objectType === 'string' ? item.objectType.trim().toUpperCase() : '';
  const organizerType = typeof item?.organizer?.objectType === 'string'
    ? item.organizer.objectType.trim().toUpperCase()
    : '';
  return objectType === 'EVENT'
    && isCanonicalObjectId(item?.id)
    && organizerType === 'USER'
    && isCanonicalObjectId(item?.organizer?.objectId);
}

const normalizeProfileVisibility = (value) => {
  const normalized = parseCanonicalVisibility(value);
  return normalized === CANONICAL_VISIBILITY.PUBLIC
    ? CANONICAL_VISIBILITY.PUBLIC
    : CANONICAL_VISIBILITY.PRIVATE;
};

const isPlainRecord = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));

const applyProfileFieldWriteResult = (currentProfile, field, value, writeResult) => {
  if (!currentProfile || !field) {
    return currentProfile;
  }
  const responseProfile = isPlainRecord(writeResult?.profile) ? writeResult.profile : null;
  const responseSummary = responseProfile && isPlainRecord(writeResult) ? writeResult : null;

  return {
    ...currentProfile,
    ...(responseSummary || {}),
    profile: {
      ...(currentProfile.profile || {}),
      [field]: value ?? '',
      ...(responseProfile || {}),
    },
  };
};

export function HomePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  const {
    settings: userPreferenceSettings,
    hasLoaded: haveUserPreferencesLoaded,
  } = useUserPreferences();

  const [activeSection, setActiveSection] = useState(SECTION_IDS.INFO);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isPublicPreviewOpen, setIsPublicPreviewOpen] = useState(false);
  const [profilePreviewUserId, setProfilePreviewUserId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendsLoaded, setFriendsLoaded] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [followersLoaded, setFollowersLoaded] = useState(false);
  const [mediaToast, setMediaToast] = useState(null);
  const [isUploadingTrack, setIsUploadingTrack] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [deletingTrackId, setDeletingTrackId] = useState(null);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  const [isReorderingTracks, setIsReorderingTracks] = useState(false);
  const [isReorderingPhotos, setIsReorderingPhotos] = useState(false);
  const [billingSubscriptionSummary, setBillingSubscriptionSummary] = useState(null);
  const [billingPublicPlans, setBillingPublicPlans] = useState([]);
  const [homeTimelineEntities, setHomeTimelineEntities] = useState([]);
  const [homeEvents, setHomeEvents] = useState([]);
  const [profileVisibility, setProfileVisibility] = useState('PUBLIC');
  const [isProfileVisibilitySaving, setIsProfileVisibilitySaving] = useState(false);
  const friendsRequestRef = useRef(0);
  const followersRequestRef = useRef(0);
  const homeTimelineEntitiesRequestRef = useRef(0);
  const homePageMountedRef = useRef(false);
  const profileFieldSaveQueueRef = useRef(Promise.resolve());
  const trackFileInputRef = useRef(null);
  const photoFileInputRef = useRef(null);

  const {
    profile: profileSummary,
    refresh: refreshProfile,
    setProfile: setProfileSummary,
  } = useUserProfileSummary(currentUser?.id, { source: 'owner' });

  const {
    posts,
    isComposerOpen,
    openCreateComposer,
    closeComposer,
    composerMode,
    postContent,
    setPostContent,
    postVisibility,
    setPostVisibility,
    editingPost,
    editContent,
    setEditContent,
    editVisibility,
    setEditVisibility,
    postAttachments,
    editAttachments,
    addCreateAttachments,
    removeCreateAttachment,
    addEditAttachments,
    removeEditAttachment,
    postAttachmentError,
    editAttachmentError,
    isSubmitting,
    maxAttachments,
    startEditingPost,
    createPost,
    updatePost,
    deletePost,
    updatePostMeta,
    removeTimelineItem,
    hasMorePosts,
    isLoadingPosts,
    isLoadingMorePosts,
    postsError,
    retryFetchPosts,
    loadMorePosts,
  } = usePosts(currentUser);

  const {
    showProfilePictureUpload,
    setShowProfilePictureUpload,
    profilePicture,
    rawProfilePicture,
    showProfilePicturePreview,
    setShowProfilePicturePreview,
    handleProfilePictureUpdate,
    shouldUseSupportAvatar,
  } = useProfilePicture(currentUser?.id);

  const {
    showProfileCoverUpload,
    setShowProfileCoverUpload,
    profileCover,
    rawProfileCover,
    handleProfileCoverUpdate,
  } = useProfileCover(currentUser?.id);

  const { bands: bandMemberships } = useUserBands(currentUser?.id);

  const profileShareIdentity = useMemo(() => ({
    username: currentUser?.username ?? profileSummary?.username ?? null,
    userId: currentUser?.id ?? null,
  }), [currentUser?.id, currentUser?.username, profileSummary?.username]);

  const {
    shareProfile,
    profileShareToast,
    clearProfileShareToast,
  } = useProfileShare(profileShareIdentity);

  const {
    profileTracks,
    setProfileTracks,
    userMediaPhotos,
    setUserMediaPhotos,
  } = useProfileMediaLibrary(currentUser?.id);
  const { followStatus } = useUserFollowStatus(currentUser?.id, {
    enabled: Boolean(currentUser?.id),
    logLabel: 'Home followers status',
  });

  useEffect(() => {
    if (!currentUser?.id || !isBillingUiEnabled()) {
      setBillingSubscriptionSummary(null);
      setBillingPublicPlans([]);
      return undefined;
    }

    let cancelled = false;
    fetchCurrentBillingSubscription()
      .then((summary) => {
        if (!cancelled) {
          setBillingSubscriptionSummary(summary || null);
        }
        if (!summary || isHomeAudioVisibleLimitKnown(summary)) {
          if (!cancelled) {
            setBillingPublicPlans([]);
          }
          return null;
        }
        return fetchPublicBillingPlans()
          .then((catalogResponse) => {
            if (!cancelled) {
              setBillingPublicPlans(billingCatalogPlans(catalogResponse));
            }
          })
          .catch((error) => {
            if (!cancelled) {
              console.warn('Failed to load billing catalog for Home media limits.', error);
              setBillingPublicPlans([]);
            }
          });
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Failed to load billing entitlements for Home media limits.', error);
          setBillingSubscriptionSummary(null);
          setBillingPublicPlans([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  useEffect(() => {
    dispatch(fetchCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    homePageMountedRef.current = true;
    return () => {
      homePageMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!currentUser?.id) {
      setProfileVisibility('PUBLIC');
      return undefined;
    }
    let cancelled = false;
    userProfileService.getProfile(currentUser.id)
      .then((profile) => {
        if (cancelled) return;
        const nextVisibility = normalizeProfileVisibility(profile?.visibility || profile?.privacy);
        setProfileVisibility(nextVisibility);
        setProfileSummary((current) => (
          current ? { ...current, visibility: nextVisibility, privacy: nextVisibility } : current
        ));
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Failed to load profile visibility.', error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, setProfileSummary]);

  useEffect(() => {
    if (!currentUser?.id) {
      setFriends([]);
      setFriendsLoaded(false);
      return undefined;
    }
    const requestId = ++friendsRequestRef.current;
    let cancelled = false;
    friendshipService.getAcceptedFriends()
      .then((rows) => {
        if (cancelled || requestId !== friendsRequestRef.current) return;
        setFriends(Array.isArray(rows) ? rows : []);
        setFriendsLoaded(true);
      })
      .catch(() => {
        if (cancelled || requestId !== friendsRequestRef.current) return;
        setFriends([]);
        setFriendsLoaded(true);
      });
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id) {
      setFollowers([]);
      setFollowersLoaded(false);
      return undefined;
    }
    const requestId = ++followersRequestRef.current;
    let cancelled = false;
    userService.getMyFollowersPage({
      limit: HOME_FOLLOWERS_RAIL_LIMIT,
      offset: 0,
    })
      .then((page) => {
        if (cancelled || requestId !== followersRequestRef.current) return;
        setFollowers(Array.isArray(page?.items) ? page.items : []);
        setFollowersLoaded(true);
      })
      .catch((error) => {
        if (cancelled || requestId !== followersRequestRef.current) return;
        console.warn('Failed to load Home followers.', error);
        setFollowers([]);
        setFollowersLoaded(true);
      });
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  const loadHomeFriendsPage = useCallback((options = {}) => (
    friendshipService.getAcceptedFriendsPage(options)
  ), []);

  const loadHomeFollowersPage = useCallback((options = {}) => (
    userService.getMyFollowersPage(options)
  ), []);

  const showMediaToast = useCallback((message, type = 'info') => {
    setMediaToast({ id: `${Date.now()}-${Math.random()}`, message, type });
  }, []);

  const clearMediaToast = useCallback(() => {
    setMediaToast(null);
  }, []);

  const handleTrackPlaybackError = useCallback((message) => {
    showMediaToast(message, 'error');
  }, [showMediaToast]);

  const handleProfileFieldSaveError = useCallback((error, context) => {
    const fieldName = context?.field || 'unknown';
    console.error(`Failed to save profile field "${fieldName}":`, error);
    showMediaToast(
      error?.response?.data?.message || t('home.editorial.saveFailed', 'Unable to save profile changes.'),
      'error'
    );
  }, [showMediaToast, t]);

  const handleProfileFieldRefreshError = useCallback((error, context) => {
    const fieldName = context?.field || 'unknown';
    console.warn(`Saved profile field "${fieldName}" but refresh failed:`, error);
  }, []);

  const handleProfileFieldWriteSuccess = useCallback((result, context) => {
    const fieldName = context?.field;
    if (!fieldName) {
      return;
    }
    setProfileSummary((current) => (
      applyProfileFieldWriteResult(current, fieldName, context.value, result)
    ));
  }, [setProfileSummary]);

  const applyReportedPlaysCount = useCallback((nextCount, track, response) => {
    const profilePlaysCount = normalizeTrackPlaysCount(response?.profilePlaysCount ?? nextCount);
    if (profilePlaysCount !== null) {
      setProfileSummary((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          profile: {
            ...(current.profile || {}),
            playsCount: profilePlaysCount,
          },
        };
      });
    }
    setProfileTracks((current) => (
      applyTrackPlaysCountByIdentity(current, track, response?.trackPlaysCount)
    ));
  }, [setProfileSummary, setProfileTracks]);

  const playbackProfileName = useMemo(() => {
    return resolveUserDisplayName({
      ...currentUser,
      username: currentUser?.username || profileSummary?.username,
      profile: profileSummary?.profile,
    }, t('home.editorial.preview.fallbackName', 'This artist'));
  }, [currentUser, profileSummary?.profile, profileSummary?.username, t]);
  const playbackProfileImage = useMemo(() => (
    rawProfilePicture?.url
    || (typeof profilePicture === 'string' ? profilePicture : profilePicture?.url)
    || null
  ), [profilePicture, rawProfilePicture?.url]);

  const {
    playingTrackId,
    trackProgressById,
    handlePlayTrack,
    handleSeekTrack,
    pauseCurrentTrack,
  } = useProfileTrackPlayback({
    userId: currentUser?.id,
    tracks: profileTracks,
    onPlaysCountChange: applyReportedPlaysCount,
    onError: handleTrackPlaybackError,
    errorMessage: t('home.editorial.media.trackPlayFailed', 'Unable to play this track.'),
    sourceLabel: playbackProfileName,
    sourceSubtitle: t('home.editorial.media.playerSource', 'Profile media'),
    sourceImage: playbackProfileImage,
  });

  const refreshHomeTimelineEntities = useCallback(async () => {
    if (!currentUser?.id) {
      homeTimelineEntitiesRequestRef.current += 1;
      setHomeTimelineEntities([]);
      setHomeEvents([]);
      return;
    }
    const requestId = ++homeTimelineEntitiesRequestRef.current;
    try {
      const items = await eventService.getCurrentUserTimeline({
        limit: ENTITY_TIMELINE_PAGE_LIMIT,
        offset: 0,
      });
      if (!homePageMountedRef.current || requestId !== homeTimelineEntitiesRequestRef.current) return;
      const normalizedItems = normalizeEntityTimelineItems(items);
      setHomeTimelineEntities(normalizedItems);
      setHomeEvents(normalizedItems.filter(isUserOrganizedEvent));
    } catch (error) {
      if (!homePageMountedRef.current || requestId !== homeTimelineEntitiesRequestRef.current) return;
      console.error('Failed to load Home timeline entity stats:', error);
      setHomeTimelineEntities([]);
      setHomeEvents([]);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    refreshHomeTimelineEntities();
  }, [refreshHomeTimelineEntities]);

  const homeEventRows = useMemo(
    () => buildScheduleEventRows(homeEvents, t),
    [homeEvents, t],
  );

  const mergeHomeEvent = useCallback((event) => {
    if (!event?.id || !isUserOrganizedEvent(event)) return;
    setHomeEvents((current) => {
      const existing = Array.isArray(current) ? current : [];
      const nextEvents = existing.some((item) => item?.id === event.id)
        ? existing.map((item) => (item?.id === event.id ? event : item))
        : [event, ...existing];
      return nextEvents;
    });
  }, []);

  const removeHomeEvent = useCallback((event) => {
    if (!event?.id) return;
    setHomeEvents((current) => (
      Array.isArray(current) ? current.filter((item) => item?.id !== event.id) : current
    ));
  }, []);

  const canManageHomeEvents = Boolean(currentUser?.id);
  const {
    isEventModalOpen,
    eventModalMode,
    eventBeingEdited,
    eventToDelete,
    isDeletingEvent,
    eventDeleteError,
    openCreateEvent: handleOpenCreateEvent,
    openEditEvent: handleOpenEditEvent,
    closeEventModal: handleCloseEventModal,
    handleEventCreated: handleHomeEventCreated,
    handleEventUpdated: handleHomeEventUpdated,
    handleEventDeleted: handleHomeEventDeleted,
    requestDeleteEvent: handleRequestDeleteHomeEvent,
    cancelDeleteEvent: handleCancelDeleteHomeEvent,
    confirmDeleteEvent: handleConfirmDeleteHomeEvent,
  } = useEventEditorActions({
    canManage: canManageHomeEvents,
    onEventCreated: mergeHomeEvent,
    onEventUpdated: mergeHomeEvent,
    onEventDeleted: removeHomeEvent,
    refreshEvents: refreshHomeTimelineEntities,
    getMutationScope: () => homeTimelineEntitiesRequestRef.current,
    logLabel: 'home event',
  });

  const {
    profileDisplayName,
    handle,
    instrumentExperiences,
    skills,
    mainInstrument,
    seekingDetails,
    commitmentDetails,
    weekSchedule,
    influencesList,
    equipmentList,
    locationLabel,
    seekingHeroLabel,
    seekingGenres,
    seekingQuote,
    statsItems,
    heroTopJoinedLabel,
    mediaTracks,
    mediaPhotos,
  } = useMemo(() => buildProfileEditorialViewModel({
    profileSummary,
    username: profileSummary
      ? (profileSummary.username || currentUser?.username || '')
      : '',
    posts,
    timelineEntities: homeTimelineEntities,
    followersCount: followStatus.followerCount,
    includePlaysStat: true,
    includeFollowersStat: Boolean(currentUser?.id),
    bandMemberships,
    friends,
    profileTracks,
    userMediaPhotos,
    trackProgressById,
    playingTrackId,
    t,
    locale,
  }), [
    bandMemberships,
    currentUser?.id,
    currentUser?.username,
    followStatus.followerCount,
    friends,
    homeTimelineEntities,
    locale,
    playingTrackId,
    posts,
    profileSummary,
    profileTracks,
    t,
    trackProgressById,
    userMediaPhotos,
  ]);
  const profileActivityStatus = profileSummary?.settings?.privacy?.showActivityStatus;
  const preferenceActivityStatus = userPreferenceSettings?.privacy?.showActivityStatus;
  const showActivityStatus = (
    haveUserPreferencesLoaded && typeof preferenceActivityStatus === 'boolean'
      ? preferenceActivityStatus
      : typeof profileActivityStatus === 'boolean'
        ? profileActivityStatus
        : false
  );
  const isOnline = showActivityStatus; // Real user is viewing their own profile when activity display is enabled.
  const profileTrackCount = Array.isArray(profileTracks) ? profileTracks.length : 0;
  const billingPlanCode = getBillingSubscriptionPlanCode(billingSubscriptionSummary);
  const billingCatalogPlan = useMemo(() => (
    (Array.isArray(billingPublicPlans) ? billingPublicPlans : [])
      .find((plan) => normalizeBillingCode(plan?.code) === billingPlanCode) || null
  ), [billingPlanCode, billingPublicPlans]);
  const hasSubscriptionHomeAudioLimit = isHomeAudioVisibleLimitKnown(billingSubscriptionSummary);
  const homeAudioVisibleLimit = useMemo(
    () => (
      hasSubscriptionHomeAudioLimit
        ? resolveHomeAudioVisibleLimit(billingSubscriptionSummary)
        : resolveHomeAudioVisibleLimit(billingCatalogPlan)
    ),
    [billingCatalogPlan, billingSubscriptionSummary, hasSubscriptionHomeAudioLimit],
  );
  const isHomeAudioTrackLimitReached = Number.isSafeInteger(homeAudioVisibleLimit)
    && profileTrackCount >= homeAudioVisibleLimit;
  const homeAudioUploadLimitTooltip = useMemo(() => {
    if (!isHomeAudioTrackLimitReached) {
      return null;
    }
    return isFreeBillingSubscription(billingSubscriptionSummary)
      ? t(
        'home.editorial.media.uploadLimitReachedFreePlan',
        'Limit for uploaded audio files is reached. Upgrade to Premium to increase your audio capacity.'
      )
      : t(
        'home.editorial.media.uploadLimitReached',
        'Limit for uploaded audio files is reached.'
      );
  }, [billingSubscriptionSummary, isHomeAudioTrackLimitReached, t]);
  const isUploadAudioDisabled = isUploadingTrack || isHomeAudioTrackLimitReached;

  const shouldShowSeekingSection = isEditing || Boolean(seekingDetails);
  const tabs = useMemo(() => buildProfileEditorialTabs(t, SECTION_IDS, {
    includeEvents: true,
    includeSeeking: shouldShowSeekingSection,
  }), [shouldShowSeekingSection, t]);

  useEffect(() => {
    if (!shouldShowSeekingSection && activeSection === SECTION_IDS.SEEKING) {
      setActiveSection(SECTION_IDS.INFO);
    }
  }, [activeSection, shouldShowSeekingSection]);

  const handleLocationSaved = useCallback(() => {
    refreshProfile().catch(() => null);
  }, [refreshProfile]);

  const handleEditProfile = useCallback(() => {
    // Enters the page's inline edit mode. The picture-upload modal lives on
    // the avatar's hover "Edit" affordance — this button toggles full
    // profile editing instead.
    setIsEditing(true);
  }, []);

  const handleOpenDiscover = useCallback(() => {
    navigate('/discover');
  }, [navigate]);

  const handlePreviewUserConnection = useCallback((friend, selectedUserId) => {
    const friendUserId = typeof selectedUserId === 'string'
      ? selectedUserId.trim()
      : (typeof friend?.userId === 'string' ? friend.userId.trim() : '');
    if (!isCanonicalObjectId(friendUserId)) {
      return;
    }
    setProfilePreviewUserId(friendUserId);
  }, []);

  const handleToggleEditing = useCallback(() => {
    setIsEditing((prev) => !prev);
  }, []);

  const handleProfileVisibilityChange = useCallback(async (nextValue) => {
    if (!currentUser?.id || isProfileVisibilitySaving) {
      return;
    }
    const nextVisibility = normalizeProfileVisibility(nextValue);
    const previousVisibility = profileVisibility;
    if (nextVisibility === previousVisibility) {
      return;
    }
    setIsProfileVisibilitySaving(true);
    setProfileVisibility(nextVisibility);
    setProfileSummary((current) => (
      current ? { ...current, visibility: nextVisibility, privacy: nextVisibility } : current
    ));
    try {
      const response = await userProfileService.updateProfileVisibility(currentUser.id, nextVisibility);
      const savedVisibility = normalizeProfileVisibility(response?.visibility || nextVisibility);
      setProfileVisibility(savedVisibility);
      setProfileSummary((current) => (
        current ? { ...current, visibility: savedVisibility, privacy: savedVisibility } : current
      ));
    } catch (error) {
      setProfileVisibility(previousVisibility);
      setProfileSummary((current) => (
        current ? { ...current, visibility: previousVisibility, privacy: previousVisibility } : current
      ));
      setMediaToast({
        id: `profile-visibility-${Date.now()}`,
        message: t('home.profileVisibility.updateFailed', 'Unable to update profile visibility.'),
        type: 'error',
      });
    } finally {
      setIsProfileVisibilitySaving(false);
    }
  }, [
    currentUser?.id,
    isProfileVisibilitySaving,
    profileVisibility,
    setProfileSummary,
    t,
  ]);

  const handleEditMainInstrument = useCallback((event) => {
    event?.preventDefault?.();
    setActiveSection(SECTION_IDS.SOUND);
    if (typeof document === 'undefined') return;

    const target = document.getElementById(INSTRUMENT_EXPERIENCE_ANCHOR_ID)
      || document.getElementById(SECTION_IDS.SOUND);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleProfileFieldSave = useCallback(async (field, value) => {
    if (!currentUser?.id || !field) {
      throw new Error('Cannot save profile field without an authenticated user.');
    }
    return enqueueProfileFieldSave(profileFieldSaveQueueRef, {
      userId: currentUser.id,
      field,
      value,
      updateProfile: userProfileService.updateProfile,
      refreshProfile,
      onError: handleProfileFieldSaveError,
      onWriteSuccess: handleProfileFieldWriteSuccess,
      onRefreshError: handleProfileFieldRefreshError,
    });
  }, [
    currentUser?.id,
    handleProfileFieldRefreshError,
    handleProfileFieldSaveError,
    handleProfileFieldWriteSuccess,
    refreshProfile,
  ]);

  const handleInstrumentExperiencesChange = useCallback(async (nextExperiences) => {
    const payload = serializeInstrumentExperiences(nextExperiences, {
      defaultYears: 'Beginner',
    });
    await handleProfileFieldSave('instrumentExperience', payload);
  }, [handleProfileFieldSave]);

  const handleProfilePictureUploaded = useCallback((newAvatarUrl) => {
    handleProfilePictureUpdate(newAvatarUrl);
  }, [handleProfilePictureUpdate]);

  const handleOpenTrackUpload = useCallback(() => {
    if (!isUploadAudioDisabled) {
      trackFileInputRef.current?.click();
    }
  }, [isUploadAudioDisabled]);

  const handleOpenPhotoUpload = useCallback(() => {
    if (!isUploadingPhoto) {
      photoFileInputRef.current?.click();
    }
  }, [isUploadingPhoto]);

  const handlePhotoFileChange = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files || files.length === 0 || !currentUser?.id) {
      return;
    }
    setIsUploadingPhoto(true);
    try {
      const uploadedPhotos = [];
      for (const file of files) {
        uploadedPhotos.push(await uploadUserMediaPhoto(file));
      }
      setUserMediaPhotos((current) => {
        const uploadedIds = new Set(uploadedPhotos.map(getMediaPhotoIdentity).filter(Boolean));
        const withoutUploaded = current.filter((photo) => {
          const id = getMediaPhotoIdentity(photo);
          return !id || !uploadedIds.has(id);
        });
        return [...sortMediaPhotosNewestFirst(uploadedPhotos), ...withoutUploaded];
      });
      setActiveSection(SECTION_IDS.GALLERY);
      showMediaToast(t('home.editorial.gallery.photoUploaded', 'Photo uploaded.'), 'success');
    } catch (error) {
      console.error('Failed to upload media photo:', error);
      showMediaToast(
        error?.message || t('home.editorial.gallery.photoUploadFailed', 'Photo upload failed.'),
        'error'
      );
    } finally {
      setIsUploadingPhoto(false);
    }
  }, [currentUser?.id, setUserMediaPhotos, showMediaToast, t]);

  const handleTrackFileChange = useCallback(async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file || isUploadingTrack) {
      return;
    }

    if (isHomeAudioTrackLimitReached) {
      showMediaToast(
        homeAudioUploadLimitTooltip
        || t('home.editorial.media.uploadLimitReached', 'Limit for uploaded audio files is reached.'),
        'error'
      );
      return;
    }

    if (file.type && !file.type.toLowerCase().startsWith('audio/')) {
      showMediaToast(t('home.editorial.media.trackInvalidType', 'Choose an audio file.'), 'error');
      return;
    }

    const maxBytes = Math.max(1, PROFILE_TRACK_MAX_SIZE_MB) * 1024 * 1024;
    if (file.size > maxBytes) {
      showMediaToast(
        t('home.editorial.media.trackTooLarge', {
          max: PROFILE_TRACK_MAX_SIZE_MB,
          defaultValue: 'Audio files must be {{max}} MB or smaller.',
        }),
        'error'
      );
      return;
    }

    setIsUploadingTrack(true);
    try {
      const uploaded = await uploadProfileTrack(file, {
        title: stripFileExtension(file.name),
      });
      const uploadedIdentity = getTrackIdentity(uploaded);
      setProfileTracks((current) => [
        uploaded,
        ...(uploadedIdentity
          ? current.filter((track) => getTrackIdentity(track) !== uploadedIdentity)
          : current),
      ]);
      setActiveSection(SECTION_IDS.MEDIA);
      showMediaToast(t('home.editorial.media.trackUploaded', 'Track uploaded.'), 'success');
    } catch (error) {
      console.error('Failed to upload profile track:', error);
      showMediaToast(t('home.editorial.media.trackUploadFailed', 'Track upload failed.'), 'error');
    } finally {
      setIsUploadingTrack(false);
    }
  }, [
    homeAudioUploadLimitTooltip,
    isHomeAudioTrackLimitReached,
    isUploadingTrack,
    setProfileTracks,
    showMediaToast,
    t,
  ]);

  const handleDeleteTrack = useCallback(async (track) => {
    const trackId = getTrackIdentity(track);
    if (!isEditing || !trackId || !track?.objectKey || deletingTrackId) {
      return;
    }

    setDeletingTrackId(trackId);
    try {
      if (playingTrackId === trackId) {
        pauseCurrentTrack(track);
      }
      await deleteProfileTrack(track.objectKey);
      setProfileTracks((current) => current.filter((item) => getTrackIdentity(item) !== trackId));
      showMediaToast(t('home.editorial.media.trackDeleted', 'Track deleted.'), 'success');
    } catch (error) {
      console.error('Failed to delete profile track:', error);
      showMediaToast(
        error?.response?.data?.message || t('home.editorial.media.trackDeleteFailed', 'Track delete failed.'),
        'error'
      );
    } finally {
      setDeletingTrackId(null);
    }
  }, [deletingTrackId, isEditing, pauseCurrentTrack, playingTrackId, setProfileTracks, showMediaToast, t]);

  const handleDeletePhoto = useCallback(async (photo) => {
    const photoId = getMediaPhotoIdentity(photo);
    if (!isEditing || !photoId || !photo?.objectKey || deletingPhotoId || isReorderingPhotos) {
      return;
    }

    setDeletingPhotoId(photoId);
    try {
      await deleteUserMediaPhoto(photo.objectKey);
      setUserMediaPhotos((current) => current.filter((item) => getMediaPhotoIdentity(item) !== photoId));
      showMediaToast(t('home.editorial.gallery.photoDeleted', 'Photo deleted.'), 'success');
    } catch (error) {
      console.error('Failed to delete media photo:', error);
      showMediaToast(
        error?.response?.data?.message || t('home.editorial.gallery.photoDeleteFailed', 'Photo delete failed.'),
        'error'
      );
    } finally {
      setDeletingPhotoId(null);
    }
  }, [deletingPhotoId, isEditing, isReorderingPhotos, setUserMediaPhotos, showMediaToast, t]);

  const handleReorderPhotos = useCallback(async (nextPhotos) => {
    if (!isEditing || isReorderingPhotos || deletingPhotoId) {
      return;
    }
    const orderedPhotoIds = (Array.isArray(nextPhotos) ? nextPhotos : [])
      .map(getMediaPhotoIdentity)
      .filter(Boolean);
    const currentPhotos = Array.isArray(userMediaPhotos) ? userMediaPhotos : [];
    const reorderedPhotos = reorderMediaPhotosByIdentity(currentPhotos, orderedPhotoIds);
    if (reorderedPhotos === currentPhotos || reorderedPhotos.length === 0) {
      return;
    }

    const objectKeys = reorderedPhotos.map((item) => item?.objectKey).filter(Boolean);
    if (objectKeys.length !== reorderedPhotos.length) {
      showMediaToast(t('home.editorial.gallery.photoReorderFailed', 'Photo reorder failed.'), 'error');
      return;
    }

    setIsReorderingPhotos(true);
    setUserMediaPhotos(reorderedPhotos);
    try {
      const persistedPhotos = await reorderUserMediaPhotos(objectKeys);
      if (Array.isArray(persistedPhotos)) {
        setUserMediaPhotos(persistedPhotos);
      }
    } catch (error) {
      console.error('Failed to reorder media photos:', error);
      setUserMediaPhotos(currentPhotos);
      showMediaToast(
        error?.response?.data?.message || t('home.editorial.gallery.photoReorderFailed', 'Photo reorder failed.'),
        'error'
      );
    } finally {
      setIsReorderingPhotos(false);
    }
  }, [deletingPhotoId, isEditing, isReorderingPhotos, setUserMediaPhotos, showMediaToast, t, userMediaPhotos]);

  const handleMoveTrack = useCallback(async (track, direction) => {
    const trackId = getTrackIdentity(track);
    if (!isEditing || !trackId || isReorderingTracks || deletingTrackId) {
      return;
    }

    const currentTracks = Array.isArray(profileTracks) ? profileTracks : [];
    const nextTracks = moveTrackByIdentity(currentTracks, trackId, direction);
    if (nextTracks === currentTracks || nextTracks.length === 0) {
      return;
    }
    const objectKeys = nextTracks.map((item) => item?.objectKey).filter(Boolean);
    if (objectKeys.length !== nextTracks.length) {
      showMediaToast(t('home.editorial.media.trackReorderFailed', 'Track reorder failed.'), 'error');
      return;
    }

    setIsReorderingTracks(true);
    setProfileTracks(nextTracks);
    try {
      const persistedTracks = await reorderProfileTracks(objectKeys);
      if (Array.isArray(persistedTracks)) {
        setProfileTracks(persistedTracks);
      }
    } catch (error) {
      console.error('Failed to reorder profile tracks:', error);
      setProfileTracks(currentTracks);
      showMediaToast(
        error?.response?.data?.message || t('home.editorial.media.trackReorderFailed', 'Track reorder failed.'),
        'error'
      );
    } finally {
      setIsReorderingTracks(false);
    }
  }, [deletingTrackId, isEditing, isReorderingTracks, profileTracks, setProfileTracks, showMediaToast, t]);

  const handleSelectBand = useCallback((band) => {
    const bandObjectId = typeof band?.id === 'string' ? band.id.trim() : '';
    const bandPath = buildBandPath(band);
    if (!bandObjectId || !bandPath || typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, TAB_IDS.BANDS);
      window.sessionStorage.setItem(BANDS_VIEW_MODE_STORAGE_KEY, BANDS_VIEW_MODE_WELCOME);
    } catch (error) {
      console.warn('Unable to persist selected band navigation intent', error);
    }

    window.dispatchEvent(
      new CustomEvent(NAVIGATE_TAB_EVENT, { detail: { tabId: TAB_IDS.BANDS, bandObjectId, path: bandPath } })
    );
    navigate(bandPath);
  }, [navigate]);

  return (
    <ProfileEditorialView
      preContent={(
        <>
          {showProfilePictureUpload && (
            <ChangeProfilePictureModal
              onClose={() => setShowProfilePictureUpload(false)}
              onUploadSuccess={handleProfilePictureUploaded}
              currentUrl={rawProfilePicture}
            />
          )}
          {showProfileCoverUpload && (
            <ChangeProfilePictureModal
              mode="profileCover"
              onClose={() => setShowProfileCoverUpload(false)}
              onUploadSuccess={handleProfileCoverUpdate}
              currentUrl={rawProfileCover}
            />
          )}
          {showProfilePicturePreview && (
            <ProfilePicturePreviewModal
              src={profilePicture}
              useSupportAvatar={shouldUseSupportAvatar}
              onClose={() => setShowProfilePicturePreview(false)}
            />
          )}
          {isLocationModalOpen && currentUser?.id && (
            <LocationModal
              userId={currentUser.id}
              initialLocation={profileSummary?.profile?.location}
              initialDisplay={profileSummary?.profile?.locationDisplay}
              onClose={() => setIsLocationModalOpen(false)}
              onSaved={handleLocationSaved}
            />
          )}
          {isPublicPreviewOpen && currentUser?.id && (
            <ProfileOverview
              userId={currentUser.id}
              viewerIdOverride={null}
              onClose={() => setIsPublicPreviewOpen(false)}
            />
          )}
          {profilePreviewUserId ? (
            <ProfileOverview
              userId={profilePreviewUserId}
              onClose={() => setProfilePreviewUserId(null)}
            />
          ) : null}
          {isEventModalOpen ? (
            <EventCreateModal
              isOpen={isEventModalOpen}
              mode={eventModalMode}
              event={eventBeingEdited}
              onClose={handleCloseEventModal}
              onCreated={handleHomeEventCreated}
              onUpdated={handleHomeEventUpdated}
              onDeleted={handleHomeEventDeleted}
            />
          ) : null}
          <ConfirmDeleteDialog
            isOpen={Boolean(eventToDelete)}
            title={t('eventsPage.editModal.deleteDialog.title')}
            description={t('eventsPage.editModal.deleteDialog.description')}
            targetName={eventToDelete?.title || null}
            confirmLabel={t('eventsPage.editModal.deleteDialog.confirm')}
            cancelLabel={t('eventsPage.editModal.deleteDialog.cancel')}
            isSubmitting={isDeletingEvent}
            errorMessage={eventDeleteError}
            onConfirm={handleConfirmDeleteHomeEvent}
            onCancel={handleCancelDeleteHomeEvent}
          />
          {profileShareToast && (
            <Notification
              key={profileShareToast.id}
              message={profileShareToast.message}
              type={profileShareToast.type}
              duration={3200}
              onClose={clearProfileShareToast}
            />
          )}
          {mediaToast && (
            <Notification
              key={mediaToast.id}
              message={mediaToast.message}
              type={mediaToast.type}
              duration={3200}
              onClose={clearMediaToast}
            />
          )}
          <input
            ref={trackFileInputRef}
            type="file"
            accept={AUDIO_MEDIA_ACCEPT}
            className="home-editorial__media-input"
            onChange={handleTrackFileChange}
            aria-hidden="true"
            tabIndex={-1}
          />
          <input
            ref={photoFileInputRef}
            type="file"
            accept={PHOTO_ACCEPT}
            className="home-editorial__media-input"
            onChange={handlePhotoFileChange}
            aria-hidden="true"
            tabIndex={-1}
            multiple
          />
        </>
      )}
      tabs={tabs}
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      tabbarAriaLabel={t('home.editorial.tabbarAria', 'Profile sections')}
      tabbarActions={(
        <EditorialIconButton
          icon={<PencilIcon width="18" height="18" />}
          label={isEditing
            ? t('home.editorial.editing.exit', 'Stop editing')
            : t('home.editorial.editing.enter', 'Edit profile')}
          active={isEditing}
          onClick={handleToggleEditing}
        />
      )}
      editing={isEditing}
      editingBanner={{
        message: t('home.editorial.editing.bannerTitle', 'Editing your profile'),
        hint: t('home.editorial.editing.bannerHint', 'Click any field on the page to update it. Changes save automatically.'),
        doneLabel: t('home.editorial.editing.done', 'Done'),
        onDone: () => setIsEditing(false),
      }}
      heroProps={{
        id: SECTION_IDS.INFO,
        editing: isEditing,
        onFieldSave: handleProfileFieldSave,
        username: profileDisplayName,
        handle,
        displayNameLabel: t('home.editorial.hero.displayNameLabel', 'Preferred name'),
        displayNamePlaceholder: t('home.editorial.hero.displayNamePlaceholder', 'Preferred name'),
        welcomeLine: profileSummary?.profile?.about ? null : t('home.editorial.hero.welcome', 'Welcome back.'),
        tagline: profileSummary?.profile?.about,
        avatarUrl: shouldUseSupportAvatar ? null : profilePicture,
        coverImageUrl: profileCover,
        isOnline,
        onlineLabel: t('home.editorial.hero.onlineNow', 'Online now'),
        joinedLabel: heroTopJoinedLabel,
        locationLabel,
        playsLabel: mainInstrument || t('home.editorial.hero.instrumentMissing', 'Instrument not selected'),
        basedIn: locationLabel,
        preferredStyle: profileSummary?.profile?.preferredStyle,
        preferredStyles: profileSummary?.profile?.preferredStyles,
        mainInstrument,
        seekingValue: seekingHeroLabel,
        seekingHref: `#${SECTION_IDS.SEEKING}`,
        seekingHeading: t('home.editorial.hero.seekingHeading', 'Seeking'),
        basedInLabel: t('home.editorial.hero.basedIn', 'Based in'),
        preferredStyleLabel: t('home.editorial.snapshot.preferredStyle', 'Preferred style'),
        preferredStyleEmptyLabel: t(
          'home.profileHeader.preferredStyle.noResults',
          PROFILE_PREFERRED_STYLE_EMPTY_LABEL,
        ),
        preferredStylePlaceholder: t(
          'home.editorial.snapshot.preferredStylePlaceholder',
          PROFILE_PREFERRED_STYLE_INPUT_PLACEHOLDER,
        ),
        preferredStyleClearLabel: t('home.profileHeader.preferredStyle.clear', 'Clear preferred style'),
        mainInstrumentLabel: t('home.editorial.snapshot.mainInstrument', 'Main instrument'),
        emptyPlaceholder: t('home.editorial.snapshot.empty', 'Not set'),
        editLabel: t('home.editorial.hero.edit', 'Edit profile'),
        shareLabel: t('home.editorial.hero.share', 'Share profile'),
        publicLabel: t('home.editorial.hero.public', 'View as public'),
        onEditProfile: handleEditProfile,
        visibility: profileVisibility,
        onVisibilityChange: handleProfileVisibilityChange,
        visibilityDisabled: isProfileVisibilitySaving,
        onEditLocation: () => setIsLocationModalOpen(true),
        mainInstrumentHref: `#${SECTION_IDS.SOUND}`,
        onEditMainInstrument: handleEditMainInstrument,
        onPublicView: currentUser?.id ? () => setIsPublicPreviewOpen(true) : undefined,
        onShareProfile: shareProfile,
        onAvatarClick: () => setShowProfilePicturePreview(true),
        onAvatarEdit: () => setShowProfilePictureUpload(true),
        onCoverEdit: isEditing ? () => setShowProfileCoverUpload(true) : undefined,
        avatarPreviewLabel: t('home.editorial.hero.avatarPreview', 'View profile picture'),
        avatarEditLabel: t('home.editorial.hero.avatarEdit', 'Edit'),
        coverEditLabel: t('home.editorial.hero.coverEdit', 'Edit cover'),
        statsItems,
      }}
      seekingProps={shouldShowSeekingSection ? {
        id: SECTION_IDS.SEEKING,
        eyebrow: t('home.editorial.seeking.eyebrow', 'Seeking'),
        title: seekingDetails ? (
          <>
            {seekingDetails.type === 'band'
              ? t('home.editorial.seeking.titleBand', 'Looking for a band.')
              : t('home.editorial.seeking.titleMembers', 'Looking for members.')}
            {seekingDetails.openToJam ? (
              <>
                <br />
                <em>{t('home.editorial.seeking.titleSub', 'Open to a jam.')}</em>
              </>
            ) : null}
          </>
        ) : (
          isEditing
            ? t('home.editorial.seeking.titleNone', 'Not looking right now.')
            : t('home.editorial.seeking.titleEmpty', 'Tell people what you are looking for.')
        ),
        side: t('home.editorial.seeking.side', 'Visible to anyone on Muzone. Helps the right people find you - bands recruiting, musicians forming projects, friends booking gigs.'),
        lookingForLabel: t('home.editorial.seeking.lookingFor', 'Looking for'),
        bandLabel: t('home.editorial.seeking.lookingForBand', 'Band'),
        membersLabel: t('home.editorial.seeking.lookingForMembers', 'Members'),
        noneLabel: t('home.editorial.seeking.lookingForNone', 'Not looking'),
        orLabel: t('home.editorial.seeking.or', 'or'),
        seekingType: seekingDetails?.type,
        openToJam: seekingDetails?.openToJam !== false,
        openToJamLabel: t('home.editorial.seeking.openToJam', "I'm open to a jam"),
        bandStyles: seekingDetails?.bandStyles || [],
        members: seekingDetails?.members || [],
        membersChipLabel: t('home.editorial.seeking.lookingForRoles', 'Looking for'),
        bandStylesViewLabel: t('home.editorial.seeking.preferredBandStyle', 'Preferred band style'),
        stylesEditorLabel: t('home.editorial.seeking.stylesEditorLabel', 'Styles you are open to'),
        membersEditorLabel: t('home.editorial.seeking.membersEditorLabel', 'Roles you need'),
        quote: seekingQuote,
        genresLabel: t('home.editorial.seeking.alsoOpen', 'Looking for'),
        genres: seekingGenres,
        emptyTitle: t('home.editorial.seeking.emptyTitle', 'Tell people what you are looking for'),
        emptyDescription: t('home.editorial.seeking.emptyDescription', 'Add a short pitch and the styles you are open to. Bands recruiting will find you in Discover.'),
        editing: isEditing,
        rawSeeking: profileSummary?.profile?.seeking,
        onFieldSave: handleProfileFieldSave,
      } : null}
      soundProps={{
        id: SECTION_IDS.SOUND,
        eyebrow: t('home.editorial.sound.eyebrow', 'Sound & gear'),
        title: (
          <>
            {t('home.editorial.sound.titleA', 'The signal chain.')}{' '}
            <em>{t('home.editorial.sound.titleB', 'What I plug in, who I steal from.')}</em>
          </>
        ),
        side: isEditing
          ? t('home.editorial.sound.mainInstrumentHint', 'Move an instrument to the top of the list to make it your main instrument.')
          : null,
        influencesTitle: t('home.editorial.sound.influences', 'Influences'),
        influences: influencesList,
        influencesHint: influencesList.length === 0 ? null : t('home.editorial.sound.influencesHint', ''),
        influencesPlaceholder: t('home.editorial.sound.influencesPlaceholder', 'The artists and bands that shaped your sound. One per line.'),
        equipmentTitle: t('home.editorial.sound.equipment', 'Equipment'),
        equipment: equipmentList,
        equipmentHint: equipmentList.length === 0 ? null : t('home.editorial.sound.equipmentHint', ''),
        equipmentPlaceholder: t('home.editorial.sound.equipmentPlaceholder', 'The instruments, amps, and gear you play through. One per line.'),
        skillsTitle: t('home.editorial.sound.instrument', 'Instrument experience'),
        skills,
        instrumentExperienceId: INSTRUMENT_EXPERIENCE_ANCHOR_ID,
        emptyTitle: t('home.editorial.sound.emptyTitle', 'Add your influences and gear'),
        emptyDescription: t('home.editorial.sound.emptyDescription', 'List who you are listening to, what you are playing through, and the instruments you cover.'),
        editing: isEditing,
        rawInfluences: profileSummary?.profile?.influences,
        rawEquipment: profileSummary?.profile?.equipment,
        instrumentExperiences,
        onFieldSave: handleProfileFieldSave,
        onInstrumentExperiencesChange: handleInstrumentExperiencesChange,
      }}
      commitmentProps={{
        id: SECTION_IDS.COMMITMENT,
        eyebrow: t('home.editorial.commitment.eyebrow', 'Commitment'),
        title: (
          <>
            {t('home.editorial.commitment.titleA', 'When I am free.')}{' '}
            <em>{t('home.editorial.commitment.titleB', 'And when I am not.')}</em>
          </>
        ),
        side: t('home.editorial.commitment.side', 'Bandmates see this when they pitch a rehearsal. Updates here update the calendar everywhere on Muzone.'),
        schedule: weekSchedule,
        weekAriaLabel: t('home.editorial.commitment.weekAria', 'Weekly availability'),
        notesLabel: t('home.editorial.commitment.notesLabel', 'Additional notes'),
        notesValue: commitmentDetails.notes
          ? commitmentDetails.notes
          : <em>{t('home.editorial.commitment.noNotes', 'No additional notes yet.')}</em>,
        notesHint: t('home.editorial.commitment.notesHint', 'Outside the slots above, you can usually be flexible with one week notice - especially for one-off gigs, last-minute fill-ins, or weekend tracking sessions.'),
        editing: isEditing,
        rawCommitment: profileSummary?.profile?.commitment,
        commitmentDetails,
        onFieldSave: handleProfileFieldSave,
      }}
      eventsProps={{
        id: SECTION_IDS.EVENTS,
        eyebrow: t('home.editorial.events.eyebrow', 'Events'),
        title: (
          <>
            {t('home.editorial.events.titleA', 'On the calendar.')}{' '}
            <em>{t('home.editorial.events.titleB', 'Your own shows, sessions, and meetups.')}</em>
          </>
        ),
        events: homeEventRows,
        onCreateEvent: canManageHomeEvents ? handleOpenCreateEvent : undefined,
        onEditEvent: canManageHomeEvents ? handleOpenEditEvent : undefined,
        onDeleteEvent: canManageHomeEvents ? handleRequestDeleteHomeEvent : undefined,
        canManageEvents: canManageHomeEvents,
        createEventLabel: t('home.editorial.events.createEvent', 'Create event'),
        emptyTitle: t('home.editorial.events.emptyTitle', 'No personal events yet'),
        emptyDescription: t('home.editorial.events.emptyDescription', 'Create an event as yourself. Band-hosted events stay on the Band page.'),
      }}
      bandsProps={{
        id: SECTION_IDS.BANDS,
        eyebrow: t('home.editorial.bands.eyebrow', 'Bands'),
        title: (
          <>
            {t('home.editorial.bands.titleA', 'The projects')}{' '}
            <em>{t('home.editorial.bands.titleB', 'I run.')}</em>
          </>
        ),
        side: t('home.editorial.bands.side', 'Bands you own or play in. Each card opens the band page with roster, posts, and tour dates.'),
        showSide: isEditing,
        bands: bandMemberships || [],
        currentUserId: currentUser?.id,
        onSelectBand: handleSelectBand,
        ownerLabel: t('home.editorial.bands.owner', 'Owner'),
        emptyTitle: t('home.editorial.bands.emptyTitle', 'No bands yet'),
        emptyDescription: t('home.editorial.bands.emptyDescription', 'Start a band or join one. Your bands appear here.'),
      }}
      galleryProps={{
        id: SECTION_IDS.GALLERY,
        eyebrow: t('home.editorial.gallery.eyebrow', 'Gallery'),
        title: (
          <>
            {t('home.editorial.gallery.titleA', 'Photos')}{' '}
            <em>{t('home.editorial.gallery.titleB', 'from the room.')}</em>
          </>
        ),
        side: t('home.editorial.gallery.ownerSide', 'Photos uploaded directly to your profile media library.'),
        photos: mediaPhotos,
        photosLabel: t('home.editorial.gallery.photos', 'Photos'),
        photosMeta: t('home.editorial.gallery.photosMeta', 'Uploaded to your media library'),
        emptyPhotosTitle: t('home.editorial.gallery.photosEmptyTitle', 'No photos yet'),
        emptyPhotosDescription: t('home.editorial.gallery.photosEmptyDescription', 'Upload photos here and they will appear in this gallery.'),
        uploadPhotoLabel: isUploadingPhoto
          ? t('home.editorial.gallery.uploadingPhoto', 'Uploading...')
          : t('home.editorial.gallery.uploadPhoto', 'Upload photo'),
        onUploadPhoto: handleOpenPhotoUpload,
        isEditing,
        onDeletePhoto: handleDeletePhoto,
        onReorderPhotos: handleReorderPhotos,
        deletingPhotoId,
        isReorderingPhotos,
        deletePhotoLabel: t('home.editorial.gallery.deletePhoto', 'Delete photo'),
        previousPhotosLabel: t('home.editorial.gallery.previousPhotos', 'Scroll photos left'),
        nextPhotosLabel: t('home.editorial.gallery.nextPhotos', 'Scroll photos right'),
      }}
      mediaProps={{
        id: SECTION_IDS.MEDIA,
        eyebrow: t('home.editorial.media.eyebrow', 'Media'),
        title: (
          <>
            {t('home.editorial.media.titleA', 'Tracks')}{' '}
            <em>{t('home.editorial.media.titleB', 'and live takes.')}</em>
          </>
        ),
        side: t('home.editorial.media.ownerSide', 'Demos and audio uploaded directly to your profile media library.'),
        tracks: mediaTracks,
        tracksLabel: t('home.editorial.media.tracks', 'Tracks'),
        tracksMeta: t('home.editorial.media.tracksMeta', 'Upload demos and live takes'),
        emptyTracksTitle: t('home.editorial.media.tracksEmptyTitle', 'No tracks yet'),
        emptyTracksDescription: t('home.editorial.media.tracksEmptyDescription', 'Upload a demo or a live take. Profiles with tracks show up more often in Discover.'),
        uploadAudioLabel: isUploadingTrack
          ? t('home.editorial.media.uploadingAudio', 'Uploading...')
          : t('home.editorial.media.uploadAudio', 'Upload audio'),
        onUploadTrack: handleOpenTrackUpload,
        uploadAudioDisabled: isUploadAudioDisabled,
        uploadAudioTooltip: homeAudioUploadLimitTooltip,
        onPlayTrack: handlePlayTrack,
        onSeekTrack: handleSeekTrack,
        isEditing,
        onDeleteTrack: handleDeleteTrack,
        onMoveTrack: handleMoveTrack,
        deletingTrackId,
        isReorderingTracks,
        deleteTrackLabel: t('home.editorial.media.deleteTrack', 'Delete track'),
        moveTrackUpLabel: t('home.editorial.media.moveTrackUp', 'Move track up'),
        moveTrackDownLabel: t('home.editorial.media.moveTrackDown', 'Move track down'),
      }}
      friendsProps={{
        id: SECTION_IDS.FRIENDS,
        eyebrow: t('home.editorial.connections.eyebrow', 'Connections'),
        title: t('home.editorial.friends.title', 'Friends'),
        side: t('home.editorial.friends.side', 'Friend requests appear in your inbox. Add people from your band rosters or by handle.'),
        showSide: isEditing,
        friends,
        messageLabel: t('home.editorial.friends.message', 'Message'),
        onSelectFriend: handlePreviewUserConnection,
        inviteTitle: t('home.editorial.friends.inviteTitle', 'Know anyone who would want to jam?'),
        inviteDescription: t('home.editorial.friends.inviteDescription', 'Send them an invite - bandmates from your bands show up here automatically.'),
        inviteActionLabel: t('home.editorial.friends.inviteAction', 'Invite friends'),
        showInviteRow: isEditing && friendsLoaded,
        emptyTitle: t('home.editorial.friends.emptyTitle', 'No friends yet'),
        emptyDescription: t('home.editorial.friends.emptyDescription', 'Search people you know and send a request.'),
        autoScrollDirection: 'right',
        viewAll: {
          label: t('home.editorial.connections.viewAll', 'View All'),
          ariaLabel: t('home.editorial.friends.viewAllAria', 'View all friends'),
          modalTitle: t('home.editorial.friends.modalTitle', 'Friends'),
          modalEyebrow: t('home.editorial.connections.eyebrow', 'Connections'),
          modalAriaLabel: t('home.editorial.friends.modalAria', 'Friends'),
          loadPage: loadHomeFriendsPage,
          disabled: !friendsLoaded,
          emptyTitle: t('home.editorial.friends.emptyTitle', 'No friends yet'),
          emptyDescription: t('home.editorial.friends.emptyDescription', 'Search people you know and send a request.'),
          loadingTitle: t('home.editorial.friends.loadingTitle', 'Loading friends...'),
          errorTitle: t('home.editorial.friends.loadErrorTitle', 'Unable to load friends'),
          errorDescription: t('home.editorial.friends.loadErrorDescription', 'Friends could not be loaded right now.'),
          retryLabel: t('home.editorial.connections.retry', 'Try again'),
          loadMoreLabel: t('home.editorial.connections.loadMore', 'Load more'),
          loadingMoreLabel: t('home.editorial.connections.loadingMore', 'Loading...'),
        },
        additionalGroups: currentUser?.id ? [{
          id: 'followers',
          title: t('home.editorial.followers.title', 'Followers'),
          people: followers,
          onSelectFriend: handlePreviewUserConnection,
          showInviteRow: false,
          emptyTitle: followersLoaded
            ? t('home.editorial.followers.emptyTitle', 'No followers yet')
            : t('home.editorial.followers.loadingTitle', 'Loading followers...'),
          emptyDescription: t('home.editorial.followers.emptyDescription', 'People who follow your profile will appear here.'),
          previousLabel: t('home.editorial.followers.previousFollowers', 'Scroll followers left'),
          nextLabel: t('home.editorial.followers.nextFollowers', 'Scroll followers right'),
          autoScrollDirection: 'left',
          viewAll: {
            label: t('home.editorial.connections.viewAll', 'View All'),
            ariaLabel: t('home.editorial.followers.viewAllAria', 'View all followers'),
            modalTitle: t('home.editorial.followers.modalTitle', 'Followers'),
            modalEyebrow: t('home.editorial.connections.eyebrow', 'Connections'),
            modalAriaLabel: t('home.editorial.followers.modalAria', 'Followers'),
            loadPage: loadHomeFollowersPage,
            disabled: !followersLoaded,
            emptyTitle: t('home.editorial.followers.emptyTitle', 'No followers yet'),
            emptyDescription: t('home.editorial.followers.emptyDescription', 'People who follow your profile will appear here.'),
            loadingTitle: t('home.editorial.followers.loadingTitle', 'Loading followers...'),
            errorTitle: t('home.editorial.followers.loadErrorTitle', 'Unable to load followers'),
            errorDescription: t('home.editorial.followers.loadErrorDescription', 'Followers could not be loaded right now.'),
            retryLabel: t('home.editorial.connections.retry', 'Try again'),
            loadMoreLabel: t('home.editorial.connections.loadMore', 'Load more'),
            loadingMoreLabel: t('home.editorial.connections.loadingMore', 'Loading...'),
          },
        }] : [],
      }}
      postsProps={{
        id: SECTION_IDS.POSTS,
        eyebrow: t('home.editorial.posts.eyebrow', 'Posts'),
        title: (
          <>
            {t('home.editorial.posts.ownerTitleA', 'What you have been')}{' '}
            <em>{t('home.editorial.posts.titleB', 'putting out there.')}</em>
          </>
        ),
        side: t('home.editorial.posts.side', 'Pinned post stays at the top of your public profile. Drafts and replies live in your inbox.'),
        showSide: isEditing,
        posts,
        currentUserId: currentUser?.id,
        isComposerOpen,
        composerMode,
        openCreateComposer,
        startEditingPost,
        closeComposer,
        editingPost,
        postContent,
        setPostContent,
        postVisibility,
        setPostVisibility,
        editContent,
        setEditContent,
        editVisibility,
        setEditVisibility,
        postAttachments,
        editAttachments,
        addCreateAttachments,
        removeCreateAttachment,
        addEditAttachments,
        removeEditAttachment,
        postAttachmentError,
        editAttachmentError,
        isSubmitting,
        maxAttachments,
        createPost,
        handleUpdatePost: updatePost,
        deletePost,
        onPostMetaChange: updatePostMeta,
        onDeleteTimelineItem: removeTimelineItem,
        hasMorePosts,
        isLoadingPosts,
        isLoadingMorePosts,
        postsError,
        onRetryPosts: retryFetchPosts,
        loadMorePosts,
        editLabel: t('home.editorial.posts.edit', 'Edit'),
        deleteLabel: t('home.editorial.posts.delete', 'Delete'),
        composeLabel: t('home.editorial.posts.compose', 'Start a post'),
        loadMoreLabel: t('home.editorial.posts.loadMore', 'Load more posts'),
        loadingMoreLabel: t('home.editorial.posts.loadingMore', 'Loading posts...'),
        loadingTitle: t('home.editorial.posts.loadingTitle', 'Loading posts...'),
        postsErrorTitle: t('home.editorial.posts.loadErrorTitle', 'Unable to load posts'),
        postsErrorDescription: t('home.editorial.posts.loadErrorDescription', 'Posts could not be loaded right now.'),
        retryPostsLabel: t('home.editorial.posts.retry', 'Try again'),
        emptyTitle: t('home.editorial.posts.emptyTitle', 'No posts yet'),
        emptyDescription: t('home.editorial.posts.emptyDescription', 'Share an update, a demo, or a thought. Your timeline lives here.'),
      }}
      ctaProps={{
        title: (
          <>
            {t('home.editorial.cta.titleA', 'Find your')}<br />
            {t('home.editorial.cta.titleB', 'next band.')}
          </>
        ),
        subtitle: t('home.editorial.cta.subtitle', 'Your profile shows up in Discover when bands search for musicians in your area. Pin a demo, fill in your schedule, and stay online.'),
        primaryLabel: t('home.editorial.cta.primary', 'Edit profile'),
        primaryAction: handleEditProfile,
        secondaryLabel: t('home.editorial.cta.secondary', 'Open Discover'),
        secondaryAction: handleOpenDiscover,
      }}
      hiddenLocationTrigger={() => setIsLocationModalOpen(true)}
    />
  );
}

export default HomePage;
